import React, { useState, useEffect } from "react";
import CryptoJS from 'crypto-js';
import { Keyring } from '@polkadot/api';
import { mnemonicValidate } from '@polkadot/util-crypto';
import './ContinueWithPassword.css';

const ContinueWithPassword = ({
    visible,
    currentUser,
    onClose,
    onConfirm,
    cypherSecret
}) => {

    const [mnemonicsPwd, setMnemonicsPwd] = useState("");
    const [error, setError] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [isUnlocking, setIsUnlocking] = useState(false);

    // handle modal show/hide with bootstrap
    useEffect(() => {
        const modalElement = document.getElementById("passswordModal");
        if (!modalElement) return;

        const modal = new window.bootstrap.Modal(modalElement);
        if (visible) modal.show();
        else modal.hide();

        return () => modal.hide();
    }, [visible]);

    let submitPassword = async () => {
        try {
            setIsUnlocking(true);
            setError(null);

            // Simulate a brief delay for better UX
            await new Promise(resolve => setTimeout(resolve, 300));

            const mnemonics = CryptoJS.AES.decrypt(cypherSecret, mnemonicsPwd).toString(CryptoJS.enc.Utf8);
           
            if (mnemonics === '') throw new Error('Wrong password entered!');
            onConfirm({ mnemonics, publicAddress: currentUser.public_address });
            resetModalState();

        } catch (e) {
            setError('Incorrect password. Please try again.');
            setIsUnlocking(false);
        }
    }

    const resetModalState = () => {
        setMnemonicsPwd("");
        setError(null);
        setShowPassword(false);
        setIsUnlocking(false);
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && mnemonicsPwd.length >= 6) {
            submitPassword();
        }
    }




    return (
        <div
            className="modal fade"
            id="passswordModal"
            tabIndex="-1"
            aria-labelledby="passwordModalLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content password-modal-content">
                    {/* Header with lock icon */}
                    <div className="modal-header border-0 pb-0">
                        <div className="w-100">
                            <div className="d-flex align-items-center mb-2">
                                <div className="password-icon-wrapper me-3">
                                    <i className="fas fa-unlock-alt"></i>
                                </div>
                                <div className="flex-grow-1">
                                    <h5 className="modal-title mb-1 fw-bold" id="passwordModalLabel">
                                        Unlock Your Wallet
                                    </h5>
                                    <p className="text-muted small mb-0">
                                        Enter your password to continue
                                    </p>
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={() => {
                                resetModalState();
                                onClose?.();
                            }}
                        ></button>
                    </div>

                    <div className="modal-body pt-3">
                        {/* Wallet Address Display */}
                        <div className="wallet-address-card mb-4">
                            <div className="d-flex align-items-start">
                                <div className="wallet-icon-badge me-3">
                                    <i className="fas fa-wallet"></i>
                                </div>
                                <div className="flex-grow-1">
                                    <label className="form-label small text-muted fw-semibold mb-1">
                                        Wallet Address
                                    </label>
                                    <div className="address-display-box">
                                        <code className="small">{currentUser?.public_address}</code>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Password Input */}
                        <div className="mb-3">
                            <label className="form-label fw-semibold d-flex align-items-center mb-2">
                                <i className="fas fa-key me-2 text-muted"></i>
                                Password
                            </label>
                            <div className="position-relative">
                                <input
                                    value={mnemonicsPwd}
                                    onChange={(e) => { 
                                        setMnemonicsPwd(e.target.value);
                                        if (error) setError(null);
                                    }}
                                    onKeyPress={handleKeyPress}
                                    className={`form-control shadow-none password-input ${
                                        error ? 'is-invalid-custom' : 
                                        mnemonicsPwd.length >= 6 ? 'is-valid-custom' : ''
                                    }`}
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    placeholder="Enter your wallet password"
                                    autoFocus
                                    disabled={isUnlocking}
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                    disabled={isUnlocking}
                                >
                                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </button>
                                {mnemonicsPwd.length > 0 && (
                                    <div className="password-strength-indicator">
                                        {mnemonicsPwd.length < 6 && (
                                            <small className="text-muted">
                                                <i className="fas fa-info-circle me-1"></i>
                                                Minimum 6 characters required
                                            </small>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="error-message mt-2">
                                    <div className="d-flex align-items-start">
                                        <i className="fas fa-exclamation-circle me-2 mt-1"></i>
                                        <div>
                                            <strong className="d-block">Authentication Failed</strong>
                                            <small>{error}</small>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Forgot Password Link */}
                            <div className="forgot-password-section mt-3">
                                <div className="d-flex align-items-center">
                                    <i className="fas fa-question-circle me-2 text-muted"></i>
                                    <a
                                        href="#"
                                        className="forgot-password-link"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onConfirm({ forgotPassword: true });
                                            resetModalState();
                                        }}
                                    >
                                        Forgot Password? Use Recovery Phrase
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer border-0 pt-0">
                        <button
                            type="button"
                            className="btn btn-outline-dark px-4 py-2"
                            data-bs-dismiss="modal"
                            onClick={() => {
                                resetModalState();
                                onClose?.();
                            }}
                            disabled={isUnlocking}
                        >
                            <i className="fas fa-times me-2"></i>
                            Cancel
                        </button>
                        <button
                            disabled={mnemonicsPwd.length < 6 || isUnlocking}
                            onClick={submitPassword}
                            type="button"
                            className="btn BtnBlack px-4 py-2"
                        >
                            {isUnlocking ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Unlocking...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-unlock me-2"></i>
                                    Unlock Wallet
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContinueWithPassword;
